import React, {
    useState,
    useCallback,
    useMemo,
    useEffect,
    useRef,
} from "react";
import { Box, Slider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IChoice, IPoll } from "@Interfaces/Entities/Poll";
import {
    readGuestVotes,
    persistGuestVotes,
    getGuestVoteForPoll,
} from "@Utils/guestVotes";
import {
    clearDoubleVoteMeta,
    DEFAULT_DOUBLE_VOTE_META,
    persistDoubleVoteMeta,
    readDoubleVoteMeta,
} from "@Utils/doubleVoteMeta";
import {
    NPSVoteContainer,
    SliderSection,
    SliderWrapper,
    LoadingSpinner,
    AuthorScaleGrid,
    AuthorScaleItem,
    AuthorScorePercentage,
    AuthorScoreBox,
    AuthorScoreFill,
    AuthorScoreNumber,
    ScaleLabelsRow,
    ScaleLabel,
} from "./NpsVote.styled";
import { createVoteGraceTimer } from "@Hooks/useVoteGraceTimer";

/**
 * Helper: Extract min label from NPS choices
 */
const extractMinLabel = (choices: IChoice[]): string => {
    if (!choices || choices.length === 0) return "0";
    const minChoice = choices[0];
    if (!minChoice?.txt) return "0";
    const parts = minChoice.txt.split(": ");
    return parts.length > 1 ? parts[1] : "0";
};

/**
 * Helper: Extract max label from NPS choices
 */
const extractMaxLabel = (choices: IChoice[]): string => {
    if (!choices || choices.length === 0) return "10";
    const maxChoice = choices[choices.length - 1];
    if (!maxChoice?.txt) return "10";
    const parts = maxChoice.txt.split(": ");
    return parts.length > 1 ? parts[1] : "10";
};

export interface NpsVoteProps {
    poll: IPoll;
    hasUserVoted: boolean;
    isDisabled: boolean;
    isHideResults: boolean;
    onVote?: (choiceId: string) => void | Promise<void>;
    onCountdownStart?: () => void;
    isAuthor?: boolean;
    allowDoubleVote?: boolean;
    doubleVoteCooldownMinutes?: number;
    canUndoVote?: boolean;
    isGuest?: boolean;
    // Hoisted timer props (optional fallback kept)
    undoCountdownEndsAt?: number | null;
    startUndoGraceTimer?: (onElapsed?: () => void) => void;
    cancelUndoGraceTimer?: () => void;
    isUndoGraceActive?: boolean;
}

/**
 * NpsVote Component
 *
 * Renders a 0-10 scale slider for voting on NPS polls.
 * Shows results as horizontal bars with vote counts and percentages.
 *
 * Behaviors:
 * - Author: Cannot vote, sees results
 * - Regular voter: Can vote once, slider disabled after voting
 * - Hide results: Hides vote counts unless user already voted
 * - Expired poll: Slider disabled
 *
 * Pattern: Similar to Choice component but for NPS polls
 */
export const NpsVote: React.FC<NpsVoteProps> = ({
    poll,
    hasUserVoted,
    isDisabled,
    onVote,
    onCountdownStart,
    isAuthor = false,
    allowDoubleVote = false,
    doubleVoteCooldownMinutes = 0,
    canUndoVote = false,
    isGuest = false,
    // Hoisted timer props (optional)
    undoCountdownEndsAt = null,
    startUndoGraceTimer,
    cancelUndoGraceTimer,
    isUndoGraceActive = false,
}) => {
    const { t } = useTranslation();
    // Local state
    const [sliderValue, setSliderValue] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Prefer parent-provided API. If missing, lazily create a pure factory timer
    // instance (doesn't use hooks) stored in a ref so we don't violate Rules of Hooks
    // and only allocate fallback state when needed.
    const localTimerRef = useRef<ReturnType<
        typeof createVoteGraceTimer
    > | null>(null);

    const startAutoSubmitGraceTimer = useCallback(
        (onElapsed?: () => void) => {
            if (startUndoGraceTimer) return startUndoGraceTimer(onElapsed);
            if (!localTimerRef.current)
                localTimerRef.current = createVoteGraceTimer();
            return localTimerRef.current.start(onElapsed);
        },
        [startUndoGraceTimer]
    );

    const cancelAutoSubmitGraceTimer = useCallback(() => {
        if (cancelUndoGraceTimer) return cancelUndoGraceTimer();
        if (localTimerRef.current) return localTimerRef.current.cancel();
        return undefined;
    }, [cancelUndoGraceTimer]);

    const isAutoSubmitGraceActive = Boolean(
        isUndoGraceActive ||
            (localTimerRef.current && localTimerRef.current.isActive)
    );
    const lastPendingValueRef = useRef<number | null>(null);
    const voteSubmittedRef = useRef(false);
    const [doubleVoteCount, setDoubleVoteCount] = useState<number>(
        DEFAULT_DOUBLE_VOTE_META.voteCount
    );
    const [doubleVoteConsumed, setDoubleVoteConsumed] = useState<boolean>(
        DEFAULT_DOUBLE_VOTE_META.consumed
    );
    const [lastVoteAt, setLastVoteAt] = useState<number>(
        DEFAULT_DOUBLE_VOTE_META.lastVoteAt
    );
    const [nowTs, setNowTs] = useState<number>(Date.now());
    // Controls whether to show the localStorage vote value in the slider
    const [showLocalStorageVote, setShowLocalStorageVote] = useState(true);
    const bumpDoubleVoteMeta = useCallback(() => {
        if (!allowDoubleVote || !isGuest) {
            return null;
        }

        const currentMeta =
            readDoubleVoteMeta(poll._id) || DEFAULT_DOUBLE_VOTE_META;
        const baseCount =
            typeof currentMeta.voteCount === "number"
                ? currentMeta.voteCount
                : doubleVoteCount;
        const nextCount = Math.min(2, Math.max(1, (baseCount || 0) + 1));
        const consumed = nextCount >= 2;
        const now = Date.now();

        setDoubleVoteCount(nextCount);
        setLastVoteAt(now);
        setDoubleVoteConsumed(consumed);
        setNowTs(now);

        persistDoubleVoteMeta(poll._id, {
            voteCount: nextCount,
            lastVoteAt: now,
            consumed,
        });

        return { nextCount, consumed, now };
    }, [allowDoubleVote, doubleVoteCount, isGuest, poll._id]);

    // Extract labels from choices
    const minLabel = useMemo(
        () => extractMinLabel(poll.choices),
        [poll.choices]
    );
    const maxLabel = useMemo(
        () => extractMaxLabel(poll.choices),
        [poll.choices]
    );

    // Get user's current choice
    const userChoice = useMemo(() => {
        if (!poll.user_choice) return null;

        const choiceId = Array.isArray(poll.user_choice)
            ? poll.user_choice[0]
            : poll.user_choice;

        return poll.choices.find((c) => String(c._id) === String(choiceId));
    }, [poll.user_choice, poll.choices]);

    // Extract numeric value from user choice
    const userChoiceValue = useMemo(() => {
        if (!userChoice) return null;
        // Extract number from choice text ("0", "5", "10", or "0: Label")
        const match = userChoice.txt.match(/^(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    }, [userChoice]);

    // Get voted value from localStorage to display indicator
    const localStorageVotedValue = useMemo(() => {
        if (!hasUserVoted) return null;
        const choiceId = getGuestVoteForPoll(poll._id);
        if (!choiceId) return null;
        const choice = poll.choices.find(
            (c) => String(c._id) === String(choiceId)
        );
        if (choice) {
            const match = choice.txt.match(/^(\d+)/);
            return match ? parseInt(match[1], 10) : null;
        }
        return null;
    }, [hasUserVoted, poll.choices, poll._id]);

    useEffect(() => {
        if (!allowDoubleVote) {
            setDoubleVoteConsumed(false);
            setLastVoteAt(0);
            setDoubleVoteCount(0);
            clearDoubleVoteMeta(poll._id);
            setNowTs(Date.now());
            return;
        }

        const meta = readDoubleVoteMeta(poll._id);
        setDoubleVoteConsumed(meta.consumed);
        setLastVoteAt(meta.lastVoteAt);
        setDoubleVoteCount(meta.voteCount);
        setNowTs(Date.now());
    }, [allowDoubleVote, poll._id]);

    const doubleVoteCoolDownMs = useMemo(
        () =>
            allowDoubleVote
                ? Math.max(0, Number(doubleVoteCooldownMinutes) * 60 * 1000)
                : 0,
        [allowDoubleVote, doubleVoteCooldownMinutes]
    );

    const cooldownActive = useMemo(
        () =>
            allowDoubleVote &&
            doubleVoteCount === 1 &&
            !canUndoVote &&
            doubleVoteCoolDownMs > 0 &&
            nowTs - lastVoteAt < doubleVoteCoolDownMs,
        [
            allowDoubleVote,
            doubleVoteCount,
            canUndoVote,
            doubleVoteCoolDownMs,
            nowTs,
            lastVoteAt,
        ]
    );

    useEffect(() => {
        if (!cooldownActive) return;
        const id = setInterval(() => setNowTs(Date.now()), 1000);
        return () => clearInterval(id);
    }, [cooldownActive]);

    const hasRemainingVote = useMemo(
        () => (allowDoubleVote ? !doubleVoteConsumed : !hasUserVoted),
        [allowDoubleVote, doubleVoteConsumed, hasUserVoted]
    );

    // Initialize slider value when user votes
    useEffect(() => {
        if (userChoiceValue !== null) {
            setSliderValue(userChoiceValue);
        }
    }, [userChoiceValue]);

    // Reset slider when cooldown expires (in double vote mode)
    useEffect(() => {
        if (!allowDoubleVote || cooldownActive) return;

        // Cooldown closed: hide stored vote in the UI but keep it in localStorage
        setShowLocalStorageVote(false);
        setSliderValue(null);
    }, [allowDoubleVote, cooldownActive, poll._id]);

    // Fallback: Submit pending vote before window closes/minimizes
    useEffect(() => {
        const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
            // Check if there's a pending vote (slider value set but not yet submitted)
            if (
                sliderValue !== null &&
                hasRemainingVote &&
                !voteSubmittedRef.current &&
                onVote &&
                isGuest &&
                !cooldownActive
            ) {
                try {
                    // Find the choice for the current slider value
                    const selectedChoice = poll.choices.find((c) => {
                        const match = c.txt.match(/^(\d+)/);
                        return match && parseInt(match[1], 10) === sliderValue;
                    });

                    if (selectedChoice) {
                        // Save to localStorage as backup BEFORE attempting submission
                        // (submission may not complete if browser closes)
                        const existingVotes = readGuestVotes();
                        const updatedVotes = existingVotes.filter(
                            (vote) => vote.pollId !== poll._id
                        );
                        updatedVotes.push({
                            pollId: poll._id,
                            choiceIds: [selectedChoice._id],
                        });
                        persistGuestVotes(updatedVotes);

                        // Attempt to submit (may not complete if browser closes)
                        await onVote(selectedChoice._id);
                        bumpDoubleVoteMeta();
                        voteSubmittedRef.current = true;
                    }
                } catch (err) {
                    console.error(
                        "[NpsVote] Failed to submit pending vote on unload:",
                        err
                    );
                    // Vote is already in localStorage as backup
                }
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [
        sliderValue,
        hasRemainingVote,
        onVote,
        poll._id,
        poll.choices,
        isGuest,
        cooldownActive,
        bumpDoubleVoteMeta,
    ]);

    // Handle vote submission (on button click or slider commit)
    const handleSliderCommit = useCallback(
        async (newValue: number) => {
            const baseBlocked =
                isSubmitting || isDisabled || isAuthor || !isGuest;
            const singleVoteBlocked =
                !allowDoubleVote && (voteSubmittedRef.current || hasUserVoted);
            const doubleVoteBlocked =
                allowDoubleVote &&
                (doubleVoteConsumed || cooldownActive || isSubmitting);

            if (baseBlocked || singleVoteBlocked || doubleVoteBlocked) {
                return;
            }

            setIsSubmitting(true);
            setError(null);

            try {
                // Find the choice corresponding to the selected value
                const selectedChoice = poll.choices.find((choice) => {
                    const match = choice.txt.match(/^(\d+)/);
                    const choiceValue = match ? parseInt(match[1], 10) : null;
                    return choiceValue === newValue;
                });

                if (!selectedChoice) {
                    throw new Error("Choice not found");
                }

                // Call vote handler if provided
                if (onVote) {
                    await onVote(selectedChoice._id);
                    const metaUpdate = bumpDoubleVoteMeta();

                    if (!metaUpdate) {
                        clearDoubleVoteMeta(poll._id);
                        setDoubleVoteCount(DEFAULT_DOUBLE_VOTE_META.voteCount);
                        setLastVoteAt(DEFAULT_DOUBLE_VOTE_META.lastVoteAt);
                        setDoubleVoteConsumed(
                            DEFAULT_DOUBLE_VOTE_META.consumed
                        );
                    }

                    // Mark vote as submitted successfully for this cycle
                    voteSubmittedRef.current = true;

                    // Save vote to localStorage ONLY after successful backend submission
                    // This ensures localStorage only contains the final committed choice
                    const existingVotes = readGuestVotes();
                    const updatedVotes = existingVotes.filter(
                        (vote) => vote.pollId !== poll._id
                    );
                    updatedVotes.push({
                        pollId: poll._id,
                        choiceIds: [selectedChoice._id],
                    });
                    persistGuestVotes(updatedVotes);
                } else {
                    // No handler provided - this will be handled by parent
                    console.warn("No vote handler provided to NpsVote");
                }
            } catch (err) {
                console.error("Failed to submit vote:", err);
                setError(t("poll.nps.vote_error") || "Failed to vote");
                setSliderValue(null); // Reset slider on error
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            allowDoubleVote,
            cooldownActive,
            doubleVoteConsumed,
            hasUserVoted,
            isAuthor,
            isDisabled,
            isGuest,
            onVote,
            poll._id,
            poll.choices,
            t,
            isSubmitting,
            bumpDoubleVoteMeta,
        ]
    );

    const scheduleAutoSubmit = useCallback(
        (value: number) => {
            if (!isGuest || !hasRemainingVote || cooldownActive) {
                return;
            }

            // If value didn't change, keep existing timer
            if (
                lastPendingValueRef.current === value &&
                isAutoSubmitGraceActive
            ) {
                return;
            }

            // Value changed - backup to localStorage immediately, then restart timer
            lastPendingValueRef.current = value;

            // Find and backup the choice immediately
            const selectedChoice = poll.choices.find((choice) => {
                const match = choice.txt.match(/^(\d+)/);
                const choiceValue = match ? parseInt(match[1], 10) : null;
                return choiceValue === value;
            });

            if (selectedChoice) {
                const existingVotes = readGuestVotes();
                const updatedVotes = existingVotes.filter(
                    (vote) => vote.pollId !== poll._id
                );
                updatedVotes.push({
                    pollId: poll._id,
                    choiceIds: [selectedChoice._id],
                });
                persistGuestVotes(updatedVotes);
            }

            cancelAutoSubmitGraceTimer && cancelAutoSubmitGraceTimer();

            // Call parent's countdown handler
            if (onCountdownStart) {
                onCountdownStart();
            }

            // Auto-submit via the provided/startable grace timer
            startAutoSubmitGraceTimer &&
                startAutoSubmitGraceTimer(() => {
                    handleSliderCommit(value);
                });
        },
        [
            cancelAutoSubmitGraceTimer,
            cooldownActive,
            handleSliderCommit,
            hasRemainingVote,
            isAutoSubmitGraceActive,
            isGuest,
            onCountdownStart,
            poll._id,
            poll.choices,
            startAutoSubmitGraceTimer,
        ]
    );

    // Calculate result bars
    const results = useMemo(() => {
        const total = poll.total_votes || 0;

        return poll.choices.map((choice, index) => {
            const match = choice.txt.match(/^(\d+)/);
            const value = match ? parseInt(match[1], 10) : index;
            const votes = choice.votes || 0;
            const percentage = total > 0 ? (votes / total) * 100 : 0;
            const isUserChoice = !!(
                userChoice && choice._id === userChoice._id
            );

            return {
                value,
                votes,
                percentage,
                isUserChoice,
                label: String(value),
            };
        });
    }, [poll.choices, poll.total_votes, userChoice]);

    // Determine if slider should be disabled
    const baseDisabled = isDisabled || isAuthor || isSubmitting || !isGuest;
    const isSliderDisabled = baseDisabled
        ? true
        : allowDoubleVote
        ? cooldownActive || doubleVoteConsumed
        : hasUserVoted || voteSubmittedRef.current;

    // AUTHOR VIEW: Show simple scale with buttons (no statistics)
    if (isAuthor) {
        return (
            <NPSVoteContainer>
                {/* Scale Buttons with Blue Bar Fill */}
                <SliderWrapper>
                    {/* Button Grid with Blue Bars */}
                    <AuthorScaleGrid>
                        {results.map((result, i) => (
                            <AuthorScaleItem key={i}>
                                {/* Percentage label above box */}
                                <AuthorScorePercentage>
                                    {Math.round(result.percentage)}%
                                </AuthorScorePercentage>
                                {/* Number Box with Blue Bar Fill */}
                                <AuthorScoreBox>
                                    {/* Blue bar fill background - vertical */}
                                    <AuthorScoreFill
                                        percentage={result.percentage}
                                    />
                                    {/* Number text (above the blue bar) */}
                                    <AuthorScoreNumber>
                                        {result.value}
                                    </AuthorScoreNumber>
                                </AuthorScoreBox>
                            </AuthorScaleItem>
                        ))}
                    </AuthorScaleGrid>

                    {/* Min/Max Labels */}
                    <ScaleLabelsRow>
                        <ScaleLabel>{minLabel}</ScaleLabel>
                        <ScaleLabel>{maxLabel}</ScaleLabel>
                    </ScaleLabelsRow>
                </SliderWrapper>
            </NPSVoteContainer>
        );
    }

    // VOTER VIEW: Show slider for voting
    return (
        <NPSVoteContainer>
            {/* Voting Section */}
            <SliderSection>
                {/* Slider */}
                <SliderWrapper>
                    <div style={{ position: "relative" }}>
                        <Slider
                            value={
                                showLocalStorageVote &&
                                localStorageVotedValue !== null
                                    ? localStorageVotedValue
                                    : sliderValue ?? 0
                            }
                            min={0}
                            max={10}
                            step={1}
                            marks={Array.from({ length: 11 }, (_, i) => ({
                                value: i,
                                label: i.toString(),
                                style:
                                    hasUserVoted && userChoiceValue === i
                                        ? {
                                              color: "#1976d2",
                                              fontWeight: "bold",
                                              fontSize: "0.9rem",
                                          }
                                        : {},
                            }))}
                            valueLabelDisplay="auto"
                            onChange={(_e, newValue) => {
                                if (typeof newValue === "number") {
                                    if (allowDoubleVote) {
                                        voteSubmittedRef.current = false;
                                    }
                                    setSliderValue(newValue);
                                }
                            }}
                            onChangeCommitted={(_e, newValue) => {
                                if (typeof newValue === "number") {
                                    setSliderValue(newValue);
                                    scheduleAutoSubmit(newValue);
                                }
                            }}
                            disabled={isSliderDisabled}
                            aria-label="NPS slider"
                        />
                    </div>
                    <ScaleLabelsRow $variant="voter">
                        <ScaleLabel>{minLabel}</ScaleLabel>
                        <ScaleLabel>{maxLabel}</ScaleLabel>
                    </ScaleLabelsRow>
                </SliderWrapper>

                {/* Loading State */}
                {isSubmitting && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.5rem",
                        }}
                    >
                        <LoadingSpinner />
                        <span style={{ fontSize: "0.875rem", color: "#999" }}>
                            {t("common.submitting") || "Submitting..."}
                        </span>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <Box
                        sx={{
                            color: "#d32f2f",
                            bgcolor: "#ffebee",
                            p: 1,
                            borderRadius: 0.5,
                            borderLeft: "3px solid #d32f2f",
                            fontSize: "0.875rem",
                        }}
                    >
                        {error}
                    </Box>
                )}
            </SliderSection>
        </NPSVoteContainer>
    );
};

export default NpsVote;
